import { useCallback, useEffect, useState } from 'react';

import { GridColDef } from '@mui/x-data-grid-pro';

import useBuildingBookNames from './useBuildingBookNames';
import { Province } from '../@types/KingdomResponse';
import { IntelTabCols, TabCols } from '../@types/Models';
import { ColumnOrder } from '../@types/State';
import {
  mandatoryCols,
  intelAgeCols,
  ovP,
  army,
  off,
  def,
  getBuildings,
  getSciences,
  defHome,
  be,
  goodSpells,
  mana,
  map,
  sotAge,
  infilAge,
  somAge,
  stealth,
  tpa,
  wages,
  wpa,
  badSpells,
  dpa,
  dsPoints,
  offHome,
  opa,
  osPoints,
  peons,
  somDef,
  horses,
  trainCredits,
  buildCredits,
  food,
  gcpa,
  prisoners,
  runes,
  solds,
  tb,
  nonPeon,
  thieves,
  wizards,
  dme,
  dspecs,
  leets,
  ospecsTraining,
  dspecsTraining,
  eliteTraining,
  thievesTraining,
  military,
  ome,
  ospecs,
  ppa,
  gcs,
  requests,
  objectives,
  draftTarget,
  draftRate,
} from '../component/Intel/columnDefinitions/gridColumnDefinitions';
import { useIntelState } from '../context/IntelState';

export default function useCols(dataSet: Province[] | undefined) {
  const { intelState } = useIntelState();
  const [bookNames, buildNames] = useBuildingBookNames();
  const [cols, setCols] = useState<IntelTabCols | undefined>(undefined);

  const rebuildCols = useCallback(() => {
    //if (!buildNames?.length || !bookNames?.length) return;
    const surveyCols: GridColDef[] = [];
    const scienceCols: GridColDef[] = [];
    let wtCol: GridColDef | undefined = undefined;
    let fortCol: GridColDef | undefined = undefined;
    if (buildNames?.length) {
      const buildings = getBuildings(buildNames);
      buildings.forEach((c) => surveyCols.push(c));
      wtCol = buildings.find((b) => b.headerName === 'WTs');
      fortCol = buildings.find((b) => b.headerName === 'Fort');
    }
    if (bookNames?.length) {
      const sciences = getSciences(bookNames);
      sciences.forEach((c) => scienceCols.push(c));
    }
    const colSet: TabCols = {
      overview: [
        ...mandatoryCols,
        ovP,
        army,
        off,
        def,
        defHome,
        tpa,
        wpa,
        stealth,
        mana,
        map,
        be,
        wages,
        objectives,
        requests,
        goodSpells,
        badSpells,
        intelAgeCols,
      ],
      war: [
        ...mandatoryCols,
        ovP,
        army,
        off,
        def,
        defHome,
        tpa,
        wpa,
        stealth,
        mana,
        map,
        peons,
        horses,
        offHome,
        somDef,
        osPoints,
        dsPoints,
        opa,
        dpa,
        objectives,
        requests,
        badSpells,
        intelAgeCols,
        infilAge,
        somAge,
        sotAge,
      ],
      resources: [
        ...mandatoryCols,
        peons,
        solds,
        horses,
        prisoners,
        trainCredits,
        buildCredits,
        gcpa,
        gcs,
        food,
        runes,
        tb,
        tpa,
        badSpells,
        sotAge,
      ],
      military: [
        ...mandatoryCols,
        ome,
        dme,
        off,
        def,
        offHome,
        defHome,
        osPoints,
        dsPoints,
        ppa,
        nonPeon,
        thieves,
        wizards,
        peons,
        solds,
        ospecs,
        dspecs,
        leets,
        horses,
        prisoners,
        ospecsTraining,
        dspecsTraining,
        eliteTraining,
        thievesTraining,
        military,
        wages,
        draftTarget,
        draftRate,
        sotAge,
        infilAge,
      ],
      survey: [...mandatoryCols, be, ...surveyCols],
      science: [...mandatoryCols, ...scienceCols],
      all: [
        ...mandatoryCols,
        ovP,
        army,
        off,
        def,
        offHome,
        defHome,
        somDef,
        osPoints,
        dsPoints,
        ome,
        dme,
        opa,
        dpa,
        tpa,
        wpa,
        ppa,
        nonPeon,
        stealth,
        mana,
        map,
        be,
        wages,
        draftTarget,
        draftRate,
        thieves,
        wizards,
        peons,
        solds,
        ospecs,
        dspecs,
        leets,
        horses,
        prisoners,
        military,
        ospecsTraining,
        dspecsTraining,
        eliteTraining,
        thievesTraining,
        trainCredits,
        buildCredits,
        gcpa,
        gcs,
        food,
        runes,
        tb,
        requests,
        objectives,
        goodSpells,
        badSpells,
        ...surveyCols,
        ...scienceCols,
        intelAgeCols,
        infilAge,
        somAge,
      ],
    };
    if (wtCol) {
      colSet.resources.push(wtCol);
      colSet.war.push(wtCol);
    }
    if (fortCol) {
      colSet.war.push(fortCol);
    }
    const newCols: IntelTabCols = {
      own: colSet,
      enemy: colSet,
    };

    for (const tabKey in intelState.columnOrder) {
      const colOrder = intelState.columnOrder[tabKey];
      colOrder.forEach((o: ColumnOrder) => {
        const ownCol = newCols.own[tabKey]?.find(
          (col: GridColDef) => col.field === o.field
        );
        if (ownCol) {
          const index = newCols.own[tabKey].indexOf(ownCol);
          if (index > -1) {
            newCols.own[tabKey].splice(index, 1);
            newCols.own[tabKey].splice(o.targetIndex - 1, 0, ownCol);
          }
        }
        const enemyCol = newCols.enemy[tabKey]?.find(
          (col: GridColDef) => col.field === o.field
        );
        if (enemyCol) {
          const index = newCols.enemy[tabKey].indexOf(enemyCol);
          if (index > -1) {
            newCols.enemy[tabKey].splice(index, 1);
            newCols.enemy[tabKey].splice(o.targetIndex - 1, 0, enemyCol);
          }
        }
      });
    }
    setCols(newCols);
  }, [bookNames, buildNames, intelState.columnOrder]);

  useEffect(() => {
    rebuildCols();
  }, [bookNames?.length, buildNames?.length, rebuildCols]);

  return cols;
}
